import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { sessionStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { configureTokenStorage, createAmplifyConfig } from '@metron-sweng/metron-auth';
import { App } from './App.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { PreferencesProvider } from './contexts/PreferencesContext.tsx';
import { TooltipProvider } from './components/ui/tooltip.tsx';
import { authConfig } from './config/auth.ts';
import './index.css';

// Required for Amplify v6 OAuth callback processing
// This enables the OAuth listener to process the code parameter when redirected back
// See: https://docs.amplify.aws/react/build-a-backend/auth/concepts/external-identity-providers/#required-for-multi-page-applications-complete-external-sign-in-after-redirect
import 'aws-amplify/auth/enable-oauth-listener';

// Validate required configuration at application bootstrap
if (!authConfig.userPoolId || !authConfig.userPoolClientId) {
  throw new Error(
    'Missing AWS Cognito configuration: VITE_USER_POOL_ID and VITE_USER_POOL_CLIENT_ID are required',
  );
}

Amplify.configure(createAmplifyConfig(authConfig));

// Use sessionStorage instead of localStorage for tokens
// This ensures tokens are cleared when the browser tab/window is closed
configureTokenStorage('sessionStorage', cognitoUserPoolsTokenProvider, sessionStorage);

// Create a client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PreferencesProvider>
            <TooltipProvider>
              <App />
            </TooltipProvider>
          </PreferencesProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
